import { isSameDay, isSameWeek, startOfWeek, format } from "date-fns";
import { toJSDate } from '@/lib/timeUtils';

/**
 * Composable for building context menu options
 * @param {Ref<Array>} events - Reference to calendar events
 * @param {Object} shiftCopy - Shift copy methods
 * @param {Object} shiftDelete - Shift delete methods
 * @param {Object} router - Vue Router instance for navigation
 * @param {Ref} selectedStore - Reference to currently selected store
 * @param {Function} openHolidayHoursDialog - Function to open holiday hours dialog
 * @returns {Object} - Context menu methods
 */
export function useContextMenu(events, shiftCopy, shiftDelete, router = null, selectedStore = null, openHolidayHoursDialog = null) {
  /**
   * Build context menu options based on click target
   * @param {String} targetType - Type of click target ('shift' or 'day')
   * @param {Object} targetData - Data of the click target
   * @returns {Array} - Array of menu options
   */
  const buildContextMenuOptions = (targetType, targetData) => {
    const options = [];

    if (!targetType || !targetData) {
      return options;
    }

    const weekStart = startOfWeek(
      new Date(targetData.start || targetData.date),
      { weekStartsOn: 0 }
    );

    // Check if there are shifts in this week
    const shiftsInWeek = events.value.filter((ev) =>
      isSameWeek(
        toJSDate(ev.start),
        new Date(targetData.date || targetData.start)
      )
    );

    // Check if there are shifts on this specific day
    const shiftsOnDay =
      targetType === "day"
        ? events.value.filter((ev) =>
            isSameDay(toJSDate(ev.start), new Date(targetData.date))
          )
        : events.value.filter((ev) =>
            isSameDay(toJSDate(ev.start), new Date(targetData.start))
          );

    if (targetType === "shift") {
      options.push({
        label: "Copy this shift",
        onClick: () => shiftCopy.copyShift(targetData),
      });

      options.push({
        label: "Copy this user's week",
        onClick: () =>
          shiftCopy.copyUserWeek(targetData.extendedProps.userId, weekStart),
      });

      options.push({
        label: "Copy all shifts this week",
        onClick: () => shiftCopy.copyAllWeek(weekStart),
      });

      // Add copy day option
      options.push({
        label: "Copy all shifts this day",
        onClick: () => shiftCopy.copyAllDay(targetData.start),
      });

      options.push({
        label: "Delete this shift",
        onClick: () => shiftDelete.deleteShiftById(targetData.id),
      });

      options.push({
        label: "Delete all shifts this day",
        onClick: () => shiftDelete.deleteShiftsByDate(targetData.start),
      });
    }

    if (targetType === "day") {
      // Add "Set Special Hours" option at the top for admins
      if (openHolidayHoursDialog && selectedStore && selectedStore.value) {
        const dateStr = format(new Date(targetData.date), 'yyyy-MM-dd');
        options.push({
          label: "Set Special Hours",
          onClick: () => {
            openHolidayHoursDialog(dateStr);
            return Promise.resolve({ success: true });
          },
        });
      }

      // Always show "Copy all shifts this week" if there are any shifts in the week
      if (shiftsInWeek.length > 0) {
        options.push({
          label: "Copy all shifts this week",
          onClick: () => shiftCopy.copyAllWeek(weekStart),
        });
      }

      // Only show day-specific options if there are shifts on this day
      if (shiftsOnDay.length > 0) {
        // Add copy day option
        options.push({
          label: "Copy all shifts this day",
          onClick: () => shiftCopy.copyAllDay(targetData.date),
        });

        options.push({
          label: "Delete all shifts this day",
          onClick: () => shiftDelete.deleteShiftsByDate(targetData.date),
        });
      }

      // Only show week-specific delete option if there are shifts in the week
      if (shiftsInWeek.length > 0) {
        options.push({
          label: "Delete all shifts this week",
          onClick: () => shiftDelete.deleteShiftsByWeek(weekStart),
        });
      }
    }

    // Always show paste options if there's something in the clipboard
    if (shiftCopy.copiedShifts.value.length && targetType === "day") {
      if (shiftCopy.copyType.value === "single")
        options.push({
          label: "Paste shift here",
          onClick: () => shiftCopy.pasteSingleShift(targetData.date),
        });
      else if (shiftCopy.copyType.value === "day_all")
        options.push({
          label: "Paste day here",
          onClick: () => shiftCopy.pasteDay(targetData.date),
        });
      else if (
        shiftCopy.copyType.value &&
        shiftCopy.copyType.value.startsWith("week")
      )
        options.push({
          label: "Paste week here",
          onClick: () => shiftCopy.pasteWeek(weekStart),
        });
    }

    return options;
  };

  return {
    buildContextMenuOptions,
  };
}
